import { useTranslation } from 'react-i18next';
import React, { useEffect, useCallback, useRef, useMemo, useState, memo } from 'react';
import cx from 'clsx';
import Skeleton from 'react-loading-skeleton';

import {
  useHiWebSocket,
  usePersistedSessionMessages,
  useOperateHiChatSessionMessages,
  useWindowVisible,
  useOfficialAccount,
  useSystemAccount,
} from '@/hooks';
import {
  debounce,
  devLog,
  getUserNameByUserInfo,
  getIsOfficialAccountById,
  CHATGPT_HI_ID,
  DEBANK_TEAM_ADDRESS,
  isSystemAddress,
  convertOfficialHiIdToOriginalId,
} from '@/utils';
import { Loading } from '@/components';

import { HiMessage, HiSession } from '../../types';
import { useFullHiUserInfoByAddr } from '../../hooks/hiUser';
import { useChargeOffer } from '../../hooks/hiOffer';
import { useHiSessionWith, useAddSession } from '../../hooks/hiSession';
import {
  useConversationMessageList,
  useFetchMessageTyping,
  invalidateMessageTyping,
} from '../../hooks/hiMessage';
import { HeaderUserInfo } from '../HeaderUserInfo';
import { HeaderOfficialAccountInfo } from '../HeaderOfficialAccountInfo';
import { HeaderSystemAccountInfo } from '../HeaderSystemAccountInfo';
import { MessageInput } from '../MessageInput';
import HiMessageItem from '../Message/HiMessageItem';
import TypingMessageItem from '../Message/TypingMessageItem';
import { HiFollowFixTip } from '../HiFollowFixTip';
import { HiOfficialFollowFixTip } from '../HiFollowFixTip/officialAccount';
import { usePutHiMessagePermission } from '../../hooks/hiMessagePermission';
import style from './index.module.css';

const HI_CONVERSATION_DETAIL_ID = 'HI-ELEMENT-DETAIL-ID';

const HEADER_HEIGHT = 88;
const MESSAGE_INPUT_OTHER_PART_HEIGHT = 91;

const HiConversationDetailInner = (props: {
  addr: string;
  sessionId: HiSession['id'];
  containerClassName?: string;
  hideOperationMenu?: boolean;
  messageInputMinRow?: number;
  hideFollowTip?: boolean;
  openInModal?: boolean;
  initialText?: string;
}) => {
  const { t } = useTranslation();

  const {
    addr: otherSideId,
    sessionId,
    containerClassName,
    hideOperationMenu,
    hideFollowTip,
    messageInputMinRow,
    openInModal,
    initialText,
  } = props;
  const isOfficialAccount = getIsOfficialAccountById(otherSideId);
  const isSystemAccount = isSystemAddress(otherSideId);
  const isFollowTipDisabled = useMemo(
    () =>
      [CHATGPT_HI_ID, DEBANK_TEAM_ADDRESS].includes(otherSideId) ||
      isSystemAccount ||
      hideFollowTip,
    [hideFollowTip, otherSideId, isSystemAccount],
  );
  const { officialAccountDetail: officialAccountInfo } = useOfficialAccount(
    convertOfficialHiIdToOriginalId(otherSideId),
  );
  const { data: SystemAccountInfo } = useSystemAccount(otherSideId);
  const { data: hiSession } = useHiSessionWith({
    session_id: sessionId,
    other_side_id: otherSideId,
    polling: true,
  });
  const userCardInfo = useFullHiUserInfoByAddr(otherSideId);
  const {
    currentUserId,
    loadingStatus,
    fetchingStatus,
    refetchHiUserInfo,
    userInfo: toUserInfo,
  } = userCardInfo;
  const { userInfo: isUserLoading } = loadingStatus;
  const { mutateAsync: chargeOffer } = useChargeOffer();
  const isVisible = useWindowVisible();

  useEffect(() => {
    const timer = window.setInterval(() => {
      isVisible && refetchHiUserInfo();
    }, 3000);
    return () => window.clearInterval(timer);
  });
  const { data: isOtherSideTyping } = useFetchMessageTyping(otherSideId);
  const isFollowed = !!toUserInfo?.is_followed;
  const otherSideAccountName = useMemo(() => {
    const officialName = isOfficialAccount && (officialAccountInfo?.name || '');

    return SystemAccountInfo?.name || officialName || getUserNameByUserInfo(toUserInfo);
  }, [isOfficialAccount, officialAccountInfo?.name, toUserInfo, SystemAccountInfo?.name]);
  const [hasReachBottomLoading, setHasReachBottomLoading] = useState(false);

  const hiMessageListRef = useRef<HTMLDivElement>(null);
  const [sessionUpdateAt, setSessionUpdateAt] = useState(0);
  const scrollToBottom = useCallback(() => {
    const ref = hiMessageListRef.current;
    ref?.scrollTo({
      top: ref.scrollHeight,
    });
  }, [hiMessageListRef]);

  // let [pageFlag, setPageFlag] = useState(0);

  const {
    messagePagesQuery,
    refetch: refetchMessage,
    readMessages,
    isLoading,
  } = useConversationMessageList({
    other_side_id: otherSideId,
    session_id: sessionId,
    direction: 'prev',
  });

  // const refetchMessageList = useCallback(async () => {
  //   setPageFlag(pageFlag + 1);

  //   refetchMessage()

  // }, [pageFlag, refetchMessage]);

  const { messageList: hiMessageList } = usePersistedSessionMessages(sessionId);
  const { putMessageOnSession } = useOperateHiChatSessionMessages();
  const putHiMessagePermission = usePutHiMessagePermission();
  const debounceFetchNextPage = debounce(() => {
    messagePagesQuery.fetchNextPage();
    setHasReachBottomLoading(false);
  }, 1000);
  useEffect(() => {
    const unreadMessageCount = hiSession?.unread_message_count;
    if (unreadMessageCount) {
      if (hiSession?.last_display_message) {
        putMessageOnSession(hiSession.id, [hiSession?.last_display_message]);
      }
      devLog(
        'HiConversation patch message ::',
        unreadMessageCount,
        hiSession?.last_display_message,
      );

      if (unreadMessageCount > 1 && hiSession?.update_at !== sessionUpdateAt) {
        // 后端消息列表还没更新，需要等一会请求
        debounceFetchNextPage();
      }
      setSessionUpdateAt(hiSession?.update_at);
    }
  }, [
    debounceFetchNextPage,
    hiSession,
    messagePagesQuery,
    otherSideId,
    putMessageOnSession,
    sessionUpdateAt,
  ]);

  // every session scroll to bottom once
  const [firstScroll, setFirstScroll] = useState(false);
  useEffect(() => {
    setFirstScroll(false);
  }, [sessionId]);
  useEffect(() => {
    if (!firstScroll && hiMessageList.length) {
      setFirstScroll(true);
      scrollToBottom();

      if (messagePagesQuery.hasPreviousPage) {
        messagePagesQuery.fetchPreviousPage();
      }
      messagePagesQuery.fetchNextPage();
    }
  }, [firstScroll, hiMessageList, messagePagesQuery, scrollToBottom, sessionId]);

  useEffect(() => {
    const scrollContainer = hiMessageListRef.current;

    const onScroll = debounce(e => {
      if (
        messagePagesQuery.isFetchingNextPage ||
        messagePagesQuery.isFetchingPreviousPage ||
        messagePagesQuery.isLoading ||
        !scrollContainer
      )
        return;
      // devLog('onScroll', scrollContainer.scrollHeight,scrollContainer.clientHeight ,scrollContainer.scrollTop)
      setHasReachBottomLoading(false);
      if (scrollContainer.scrollTop > -200) {
        // devLog('Hi Scroll', 'reach bottom')
        messagePagesQuery.fetchNextPage();
        setHasReachBottomLoading(true);
      }
      if (
        scrollContainer.scrollHeight <
        scrollContainer.clientHeight - scrollContainer.scrollTop + 200
      ) {
        // devLog('Hi Scroll', 'reach top')
        messagePagesQuery.fetchPreviousPage();
      }
    }, 500);
    scrollContainer?.addEventListener('scroll', onScroll);
    return () => scrollContainer?.removeEventListener('scroll', onScroll);
  }, [messagePagesQuery, scrollToBottom]);

  const { socket } = useHiWebSocket();
  useEffect(() => {
    if (!socket?.connected) return;

    const onAnyEvent = (
      event: any,
      payload: {
        data: HiMessage & {
          session_id: HiSession['id'];
        };
      },
    ) => {
      if (event === 'message' && payload?.data?.session_id === sessionId) {
        devLog('HiConversation onMessagePushed ::', event, payload);
        // 后端消息列表还没更新，需要等一会请求
        debounceFetchNextPage();
        putMessageOnSession(sessionId, [payload.data]);
        invalidateMessageTyping(otherSideId);
      } else if (event === 'user_message_permission' && payload?.data) {
        putHiMessagePermission(payload?.data as any);
      }
    };

    socket.onAny(onAnyEvent);
    return () => {
      socket.offAny(onAnyEvent);
    };
  }, [
    sessionId,
    socket?.connected,
    scrollToBottom,
    socket,
    messagePagesQuery,
    putMessageOnSession,
    putHiMessagePermission,
    debounceFetchNextPage,
    otherSideId,
  ]);

  // fallback to websocket disconnected
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!socket?.connected && isVisible) {
        refetchMessage();
      }
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, otherSideId]);

  const price = useMemo(
    () => (isFollowed ? undefined : toUserInfo?.offer_price),
    [toUserInfo?.offer_price, isFollowed],
  );

  const onChargeOffer = async (
    offerId: string,
    sessionId: HiSession['id'],
    messageId: HiMessage['id'],
  ) => {
    if (offerId) {
      await chargeOffer({
        offer_id: offerId,
        session_id: sessionId,
        message_id: messageId,
      });
    }
  };

  const onReadMessage = useCallback(
    (id: HiMessage['id']) => {
      readMessages([id]);
    },
    [readMessages],
  );

  const [textContentHeight, setTextContentHeight] = useState(17);

  const messageListHeight = useMemo(() => {
    return `calc(100% - ${textContentHeight + MESSAGE_INPUT_OTHER_PART_HEIGHT + HEADER_HEIGHT}px)`;
  }, [textContentHeight]);

  const offerPrice = useMemo(() => price && price / 10000, [price]);

  return (
    <div
      className={cx(style.hiConversationDetail, containerClassName)}
      id={HI_CONVERSATION_DETAIL_ID}>
      <div className={style.userCard}>
        {isSystemAccount ? (
          <HeaderSystemAccountInfo
            account={SystemAccountInfo}
            hiSession={hiSession}
            hideOperationMenu={hideOperationMenu}
          />
        ) : isOfficialAccount ? (
          <HeaderOfficialAccountInfo
            hiSession={hiSession}
            officialAccountInfo={officialAccountInfo}
            hideOperationMenu={hideOperationMenu}
          />
        ) : userCardInfo ? (
          <HeaderUserInfo
            hiSession={hiSession}
            userCardInfo={userCardInfo}
            address={otherSideId}
            loadingStatus={loadingStatus}
            fetchingStatus={fetchingStatus}
            hideOperationMenu={hideOperationMenu}
          />
        ) : null}
      </div>
      <div
        className={cx(style.messageList, 'hi-conversation-message-list')}
        style={{ height: messageListHeight }}>
        {isFollowTipDisabled ? null : isOfficialAccount ? (
          <HiOfficialFollowFixTip officialAccountInfo={officialAccountInfo} />
        ) : (
          !!userCardInfo && <HiFollowFixTip address={otherSideId} />
        )}
        <div className={style.messageListInner} ref={hiMessageListRef}>
          {/* 空div填充空间 */}
          <div className={style.fullSpace} />
          {/* 空div填充空间 */}
          {hiMessageList.length || isLoading ? null : (
            <div className={style.emptyListPlaceholder}>{t('HiConversationDetail.utfwxv')}</div>
          )}
          {messagePagesQuery.isFetchingNextPage && hasReachBottomLoading ? (
            <Loading className={style.ladingNext} />
          ) : null}

          {isOtherSideTyping ? <TypingMessageItem /> : null}

          {hiMessageList && sessionId
            ? hiMessageList.map(hiMessage => (
                <HiMessageItem
                  key={hiMessage.id}
                  item={hiMessage}
                  readMessage={onReadMessage}
                  currentUserId={currentUserId}
                  otherSideId={otherSideId}
                  sessionId={sessionId}
                  chargeOffer={onChargeOffer}
                  otherSideAccountName={otherSideAccountName!}
                  otherSideUser={toUserInfo}
                />
              ))
            : null}

          {messagePagesQuery.isFetchingPreviousPage ? <Loading /> : null}
        </div>
      </div>
      <div className={style.messageInput}>
        <MessageInput
          initialText={initialText}
          refetchHiUserInfo={refetchHiUserInfo}
          otherSideUserCardInfo={userCardInfo}
          parentElementId={HI_CONVERSATION_DETAIL_ID}
          sessionId={sessionId}
          systemAccountInfo={isSystemAccount ? SystemAccountInfo : undefined}
          officialAccountInfo={isOfficialAccount ? officialAccountInfo : undefined}
          price={offerPrice}
          socketConnected={socket?.connected}
          isLoading={isUserLoading}
          minInputLineCount={messageInputMinRow}
          onScrollHeightChange={setTextContentHeight}
          openInModal={openInModal}
        />
      </div>
    </div>
  );
};

const HiConversationDetail = memo(
  (_props: {
    addr: string;
    sessionId?: string | number;
    containerClassName?: string;
    hideOperationMenu?: boolean;
    messageInputMinRow?: number;
    hideFollowTip?: boolean;
    openInModal?: boolean;
    initialText?: string;
  }) => {
    const {
      addr: other_side_id,
      sessionId: sessionIdFromProps,
      containerClassName,
      hideOperationMenu,
      messageInputMinRow,
      hideFollowTip,
      openInModal,
      initialText,
    } = _props || {};
    const { data, mutateAsync: addSession } = useAddSession();
    useEffect(() => {
      if (!sessionIdFromProps && other_side_id) {
        addSession({ otherSideId: other_side_id });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const sessionId = useMemo(
      () => sessionIdFromProps || data?.session?.id,
      [data?.session?.id, sessionIdFromProps],
    );

    return sessionId ? (
      <HiConversationDetailInner
        addr={other_side_id}
        sessionId={sessionId}
        containerClassName={containerClassName}
        hideOperationMenu={hideOperationMenu}
        hideFollowTip={hideFollowTip}
        messageInputMinRow={messageInputMinRow}
        openInModal={openInModal}
        initialText={initialText}
      />
    ) : (
      <div className={cx(style.hiConversationDetail, containerClassName)}>
        <Skeleton height={88} />
        <div className={cx(style.messageList, 'hi-conversation-message-list')} />
        <Skeleton height={163} />
      </div>
    );
  },
);

export default HiConversationDetail;
